import { ResolveFn } from '@angular/router';
import { LANE_DATA } from '../lanes/lane-resolver';
import { CarrierDeparture, CarrierPortPage } from './carrier-types';

const DEPARTURES_SHOWN = 8;
// Same as the lane and Sealanes generators: one ship's call at a port can carry ETDs a few days apart
// in the feed, and each lane keeps its own, so departures of one ship within this many days are one call.
const CALL_SPREAD_DAYS = 5;
const daysBetween = (a: string, b: string) => Math.abs(Date.parse(b) - Date.parse(a)) / 86400000;

// Builds one carrier × port page from the origin port's lane chunk: that carrier's row on every lane,
// and its departures from the lanes' next-sailing lists, merged by ship call.
// carrier-copy is imported on demand: it pulls in the carrier index, which should stay out of the main bundle.
export const carrierPortResolver: ResolveFn<CarrierPortPage | undefined> = async route => {
  const { findCarrier, findCarrierPort } = await import('./carrier-copy');
  const entry = findCarrierPort(route.paramMap.get('carrier'), route.paramMap.get('port'));
  const carrier = findCarrier(route.paramMap.get('carrier'));
  if (!entry || !carrier) return undefined;
  const lanes = await LANE_DATA[entry.origin]();
  const routes = lanes
    .flatMap(l => l.carriers.filter(c => c.name === carrier.name).map(c => ({ dest: l.dest, sailings: c.sailings, fastest: c.fastest, typical: c.typical, mainVia: c.mainVia })))
    .sort((a, b) => b.sailings - a.sailings || a.typical - b.typical);
  const sailings = lanes
    .flatMap(l => l.next.filter(s => s.carrier === carrier.name).map(s => ({ ...s, dest: l.dest })))
    .sort((a, b) => a.etd.localeCompare(b.etd));
  const departures: CarrierDeparture[] = [];
  for (const s of sailings) {
    const d = departures.find(x => x.vessel === s.vessel && daysBetween(x.etd, s.etd) <= CALL_SPREAD_DAYS);
    if (d) d.to.push({ dest: s.dest, eta: s.eta, days: s.days });
    else departures.push({ etd: s.etd, vessel: s.vessel, voyage: s.voyage, to: [{ dest: s.dest, eta: s.eta, days: s.days }], via: s.via });
  }
  const hubCount = new Map<string, number>();
  for (const r of routes) hubCount.set(r.mainVia, (hubCount.get(r.mainVia) ?? 0) + 1);
  return {
    carrier,
    origin: entry.origin,
    window: lanes[0]?.window ?? { from: '', to: '' },
    routes,
    departures: departures.map(d => ({ ...d, to: d.to.sort((a, b) => a.days - b.days) })).slice(0, DEPARTURES_SHOWN),
    hubs: [...hubCount.entries()].sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count })),
  };
};
